/* eslint-disable sonarjs/no-duplicate-string */
//#region //! General
export const BREAKPOINTS = {
  t: 960,
  d: 1440,
};

export const BREAKPOINTS_LIST = Object.keys(BREAKPOINTS);

export const PASSWORD_MIN_LENGTH = 10;

export const FOCUSABLE_ELEMENTS_SELECTOR =
  '[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, [contenteditable], [tabindex]:not([tabindex="-1"])';

export const PROPOSED_SELLS_CHAR_COUNT = 44;
export const PROPOSED_SELLS_ROWS_LIMIT = 13;
export const ALTERNATIVE_LINKS = ["mailto:", "tel:", "sms:", "callto:"];
export const CONTACT_US_EMAIL = "SMACustomization@allspringglobal.com";
export const ERROR_MESSAGE = {
  TITLE: "ERROR",
  SUBTITLE: "Oops. Something went wrong.",
  DESCRIPTION: {
    GENERAL: `If the error persists, please contact us: ${CONTACT_US_EMAIL}`,
    NOT_FOUND:
      "We couldn't find that page. It may have been moved or deleted. Check the URL or return to the All Proposals page.",
    UNAUTHORIZED:
      "Your user permissions prevent you from being able to view this page or there is an issue with your user session.",
  },
};
//#endregion

//#region //! API ROUTES
export const API_SOURCE =
  process.env.STORYBOOK_API_SOURCE || process.env.REACT_APP_API_SOURCE;
export const API_BASE_URL =
  API_SOURCE === "MOCK"
    ? process.env.STORYBOOK_MOCK_API_BASE_URL ||
      process.env.REACT_APP_MOCK_API_BASE_URL
    : process.env.STORYBOOK_CLIENT_API_BASE_URL ||
      process.env.REACT_APP_CLIENT_API_BASE_URL;
export const API_KEY =
  API_SOURCE === "MOCK"
    ? process.env.STORYBOOK_MOCK_API_KEY || process.env.REACT_APP_MOCK_API_KEY
    : process.env.STORYBOOK_CLIENT_API_KEY ||
      process.env.REACT_APP_CLIENT_API_KEY;

export const ADMIN_API_BASE_URL = process.env.REACT_APP_ADMIN_API_BASE_URL;
export const PDF_API_BASE_URL = process.env.REACT_APP_PDF_API_BASE_URL;

const STATIC_API_ROUTES = {
  MOCK: {
    SPONSOR_DETAIL: "get-strategy-sponsor-detail",
    REFERENCE_DATA: "rolled-up-generic-reference-info",
    SAVE_PROPOSAL: "tplan-template-acct/save",
    PROPOSAL_LIST: "proposals",
    PROPOSAL_DETAIL: "proposal-details",
    PROPOSAL_COMMENT: "proposal-comment",
    OUTPUT_REPORT_FORMATTING: "output-formatting",
    OUTPUT_REPORT_DETAIL: "get-output",
    STATE_EXCLUSIONS: "state-exclusions",
    SECTOR_EXCLUSIONS: "sector-exclusions",
    INDUSTRY_EXCLUSIONS: "industry-group-exclusions",
    ESG_SCREENS_EXCLUSIONS: "esg-screens-exclusions",
    WEEKLY_REPORTS: "get-weekly-reports",
    CLIENT_QUESTIONNAIRES_LIST: "client-questionnaire-listing",
    CLIENT_QUESTIONNAIRES_DETAIL: "client-questionnaire-details",
    SAVE_QUESTIONNAIRE: "client-questionnaire",
    METADATA: "metadata",
    GET_CUSTOM_INDEXES: "get-custom-indexes",
  },
  CLIENT: {
    SPONSOR_DETAIL: "get-strategy-sponsor-detail",
    REFERENCE_DATA: "rolled-up-generic-reference-info",
    SAVE_PROPOSAL: "tplan-template-acct/save",
    PROPOSAL_LIST: "proposal-listing",
    PROPOSAL_DETAIL: "proposal-details",
    PROPOSAL_COMMENT: "proposal-comment",
    OUTPUT_REPORT_FORMATTING: "output-formatting",
    OUTPUT_REPORT_DETAIL: "get-output",
    STATE_EXCLUSIONS: "state-exclusions",
    SECTOR_EXCLUSIONS: "sector-exclusions",
    INDUSTRY_EXCLUSIONS: "industry-group-exclusions",
    ESG_SCREENS_EXCLUSIONS: "esg-screens-exclusions",
    WEEKLY_REPORTS: "get-weekly-reports",
    CLIENT_QUESTIONNAIRES_LIST: "client-questionnaire-listing",
    CLIENT_QUESTIONNAIRES_DETAIL: "client-questionnaire-details",
    SAVE_QUESTIONNAIRE: "client-questionnaire",
    METADATA: "metadata",
    GET_CUSTOM_INDEXES: "get-custom-indexes",
  },
};

export const API_ADMIN_ROUTES = {
  ADMIN_LIST: "users",
  ADMIN_DISABLE: "users/disable",
  ADMIN_LOGOUT: "users/signout",
};

const getPDFAPIFullRoute = (route) => `${PDF_API_BASE_URL}${route}`;
// removed metadata for the time being as it throws a 500
export const PDF_API_ROUTES = {
  REPORT_CUSTOMIZER: () => getPDFAPIFullRoute(`start-pdf-reports`),
  REPORT_METADATA: ({ transitionId }) =>
    getPDFAPIFullRoute(`reports/${transitionId}/metadata`),
  REPORT_STATUS: (proposalId) =>
    getPDFAPIFullRoute(`check-pdf-report-ready/?id=${proposalId}`),
  REPORT_COMPLETE: (proposalId) =>
    getPDFAPIFullRoute(`fetch-pdf-report/?id=${proposalId}`),
};

export const API_ROUTES = STATIC_API_ROUTES[API_SOURCE];
//#endregion

//#region //! PAGES
const BASE_ROUTES = {
  admin: "admin",
  proposals: "proposals",
  clientQuestionnaires: "client-questionnaires",
  weeklyReports: "weekly-reports",
};

export const PAGE_ROUTES = {
  generic: { getFullPath: () => "/" },

  // Authentication + Authorization
  signin: { baseRoute: "signin", getFullPath: () => `/signin` },
  signout: { baseRoute: "signout", getFullPath: () => `/signout` },
  unauthorized: {
    baseRoute: "unauthorized",
    getFullPath: () => `/unauthorized`,
  },

  // User account actions
  requestPassword: {
    baseRoute: "request-password",
    getFullPath: () => `/request-password`,
  },
  resetPassword: {
    baseRoute: "reset-password",
    getFullPath: () => `/reset-password`,
  },
  newUser: {
    baseRoute: "new-user",
    getFullPath: () => `/new-user`,
  },

  // Admin
  admin: {
    baseRoute: BASE_ROUTES.admin,
    getFullPath: () => `/${BASE_ROUTES.admin}`,
  },
  users: {
    baseRoute: BASE_ROUTES.admin,
    getFullPath: () => `/${BASE_ROUTES.admin}/users`,
  },
  userCreation: {
    baseRoute: BASE_ROUTES.admin,
    route: "users/add",
    getFullPath: () => `/${BASE_ROUTES.admin}/users/add`,
  },

  // Proposals
  proposalListing: {
    baseRoute: BASE_ROUTES.proposals,
    getFullPath: () => `/${BASE_ROUTES.proposals}`,
  },
  proposal: {
    baseRoute: BASE_ROUTES.proposals,
    route: ":id",
    getFullPath: (id) => `/${BASE_ROUTES.proposals}/${id}`,
  },
  proposalCreation: {
    baseRoute: BASE_ROUTES.proposals,
    route: "add",
    getFullPath: () => `/${BASE_ROUTES.proposals}/add`,
  },

  // Proposals related routes
  duplicate: {
    baseRoute: "duplicate",
    route: ":id",
    getFullPath: (id) => `/duplicate/${id}`,
  },
  report: {
    baseRoute: "reports",
    route: ":id",
    getFullPath: (id) => `/reports/${id}`,
  },

  // Client Questionnaires
  clientQuestionnaireListing: {
    baseRoute: BASE_ROUTES.clientQuestionnaires,
    getFullPath: () => `/${BASE_ROUTES.clientQuestionnaires}`,
  },
  clientQuestionnaire: {
    baseRoute: BASE_ROUTES.clientQuestionnaires,
    route: ":id",
    getFullPath: (id) => `/${BASE_ROUTES.clientQuestionnaires}/${id}`,
  },
  clientQuestionnaireCreation: {
    baseRoute: BASE_ROUTES.clientQuestionnaires,
    route: "add",
    getFullPath: (id) => `/${BASE_ROUTES.clientQuestionnaires}/add/${id}`,
  },
  clientQuestionnaireCreationWithProposal: {
    baseRoute: BASE_ROUTES.clientQuestionnaires,
    route: "add/:proposalId",
    getFullPath: (proposalId) =>
      `/${BASE_ROUTES.clientQuestionnaires}/add/${proposalId}`,
  },

  // Weekly Reports
  weeklyReports: {
    baseRoute: BASE_ROUTES.weeklyReports,
    getFullPath: () => `/${BASE_ROUTES.weeklyReports}`,
  },
};
//#endregion

//#region //! TABLES
export const PROPOSALS_COLUMNS = [
  {
    label: "Proposal Name",
    key: "name",
    isSortable: true,
  },
  {
    label: "Advisor Name",
    key: "advisor",
    isSortable: true,
  },
  {
    label: "Last Updated/UTC",
    key: "date",
    isSortable: true,
  },
  {
    label: "Strategy",
    key: "strategy",
    isSortable: true,
  },
  {
    label: "Status",
    key: "status",
    isSortable: true,
  },
  {
    label: "View Actions",
    key: "actions",
    isSortable: false,
    isHidden: true,
  },
];

export const CLIENT_QUESTIONNAIRES_COLUMNS = [
  {
    label: "Client Name",
    key: "name",
    isSortable: true,
  },
  {
    label: "Advisor Name",
    key: "advisor",
    isSortable: true,
  },
  {
    label: "Proposal Name",
    key: "proposalName",
    isSortable: true,
  },
  {
    label: "Strategy",
    key: "strategy",
    isSortable: true,
  },
  {
    label: "Created On",
    key: "date",
    isSortable: true,
  },
  {
    label: "View Actions",
    key: "actions",
    isSortable: false,
    isHidden: true,
  },
];

export const ADMIN_USERS_COLUMNS = [
  {
    label: "First Name",
    key: "firstName",
    isSortable: true,
  },
  {
    label: "Last Name",
    key: "lastName",
    isSortable: true,
  },
  {
    label: "Email",
    key: "email",
    isSortable: true,
  },
  {
    label: "Status",
    key: "statusText",
    isSortable: true,
  },
  {
    label: "Actions Menu",
    key: "actions",
    isSortable: false,
    isHidden: true,
  },
];
//#endregion

//#region //! PROPOSALS and CLIENT QUESTIONNAIRES

export const FUNDING_SOURCE_TYPES = {
  cashOnly: "Cash only",
  securitiesOnly: "Securities only",
  cashAndSecurities: "Cash and securities",
};

export const RESTRICTION_OPTIONS = {
  buy: "buy",
  sell: "sell",
  trade: "trade",
  hold: "hold",
  none: "none",
};

export const EXCLUSIONS_CONFIG = {
  state: {
    maxItems: 10,
    maxValue: 0.2,
    field: "state",
  },
  sector: {
    maxValue: 0.15,
    field: "sector",
  },
  gics_industry_group: {
    maxValue: 0.15,
    field: "gics_industry_group",
  },
  esg_screens: {
    field: "name",
  },
};

export const REMOVED_STATES = ["Guam", "Cash", "Puerto Rico", "Virgin Islands"];

export const STRATEGY_LABEL = {
  corp: "Corporate Bonds",
  muni: "Municipal Bonds",
};

export const REPORT_HEADING_OVERRIDES = {
  equity: {
    cash: {
      "Sectors (GICS)": {
        Benchmark: "Benchmark***",
      },
      "Sectors (Risk Exposure)": {
        Benchmark: "Benchmark***",
      },
      "Style (Risk Exposure)": {
        Benchmark: "Benchmark***",
      },
      "Attribution of Tracking Error": {
        "Target Portfolio": "Target Portfolio*",
      },
      portfolioCharacteristics: {
        "Target Portfolio": "Target Portfolio*",
        Benchmark: "Benchmark**",
      },
      "portfolioCharacteristics-ose": {
        "Target Portfolio": "Target Portfolio*",
      },
      "sectors-gics-ose": {
        "Target Portfolio": "Target Portfolio[1]",
      },
    },
    transition: {
      portfolioCharacteristics: {
        Benchmark: "Benchmark***",
      },
      "portfolioCharacteristics-ose": {
        Benchmark: "Benchmark***",
      },
      "Sectors (Risk Exposure)": {
        "Existing Portfolio": "Existing Portfolio**",
        Benchmark: "Benchmark****",
      },
      "Sectors (GICS)": {
        "Existing Portfolio": "Existing Portfolio**",
        Benchmark: "Benchmark****",
      },
      "Style (Risk Exposure)": {
        "Existing Portfolio": "Existing Portfolio**",
        Benchmark: "Benchmark****",
      },
      "attributionTrackingError-ose": {
        "Existing Portfolio": "Existing Portfolio*",
        "Balance Tax Liability & Tracking Error": [
          "Balance Tax Liability &",
          "Tracking Error",
        ],
      },
      "Attribution of Tracking Error": {
        "Balance Tax Liability & Tracking Error": [
          "Balance Tax Liability &",
          "Tracking Error",
        ],
      },
    },
  },
  corp: {
    transition: {
      "portfolioCharacteristics-os": {
        "Maintain and Reinvest Proceeds":
          "Full Liquidation for Pre-Existing Portfolio",
        "Reference Index": "Benchmark",
      },
      portfolioCharacteristics: {
        "Maintain and Reinvest Proceeds":
          "Full Liquidation for Pre-Existing Portfolio",
        "Reference Index": "Reference Index***",
      },
      transactionSummary: {
        "Maintain And Reinvest Proceeds":
          "Existing Portfolio (Maintain & Reinvest Proceeds)",
      },
      maturity: {
        "Reference Index": "Reference Index***",
      },
      creditQuality: {
        "Reference Index": "Reference Index***",
      },
      esgRating: {
        "Reference Index": "Reference Index***",
      },
      keyRateDurations: {
        "Reference Index": "Reference Index***",
      },
      Sector: {
        "Reference Index": "Reference Index***",
      },
    },
    cash: {
      "portfolioCharacteristics-os": {
        "Reference Index": "Reference Index",
      },
      portfolioCharacteristics: {
        "Reference Index": "Reference Index**",
      },
      maturity: {
        "Reference Index": "Reference Index**",
      },
      Maturity: {
        "Reference Index": "Reference Index**",
      },
      creditQuality: {
        "Reference Index": "Reference Index**",
      },
      "Credit Quality": {
        "Reference Index": "Reference Index**",
      },
      esgRating: {
        "Reference Index": "Reference Index**",
      },
      "ESG Rating": {
        "Reference Index": "Reference Index**",
      },
      keyRateDurations: {
        "Reference Index": "Reference Index**",
      },
      "Key Rate": {
        "Reference Index": "Reference Index**",
      },
      Sector: {
        "Reference Index": "Reference Index**",
      },
    },
  },
  muni: {
    transition: {
      "portfolioCharacteristics-os": {
        "Maintain and Reinvest Proceeds":
          "Full Liquidation for Pre-Existing Portfolio",
        "Reference Index": "Benchmark",
      },
      portfolioCharacteristics: {
        "Maintain and Reinvest Proceeds":
          "Full Liquidation for Pre-Existing Portfolio",
        "Reference Index": "Reference Index***",
      },
      maturity: {
        "Reference Index": "Reference Index***",
      },
      creditQuality: {
        "Reference Index": "Reference Index***",
      },
      keyRateDurations: {
        "Reference Index": "Reference Index***",
      },
      Sector: {
        "Reference Index": "Reference Index***",
      },
      stateExposure: {
        "Reference Index": "Reference Index***",
      },
    },
    cash: {
      "portfolioCharacteristics-os": {
        "Reference Index": "Reference Index",
      },
      portfolioCharacteristics: {
        "Reference Index": "Reference Index**",
      },
      maturity: {
        "Reference Index": "Reference Index**",
      },
      Maturity: {
        "Reference Index": "Reference Index**",
      },
      "Credit Quality": {
        "Reference Index": "Reference Index**",
      },
      creditQuality: {
        "Reference Index": "Reference Index**",
      },
      "Key Rate": {
        "Reference Index": "Reference Index**",
      },
      keyRateDurations: {
        "Reference Index": "Reference Index**",
      },
      Sector: {
        "Reference Index": "Reference Index**",
      },
      stateExposure: {
        "Reference Index": "Reference Index**",
      },
    },
  },
  treasury: {
    cash: {
      "portfolioCharacteristics-os": {
        "Reference Index": "Reference Index",
      },
      portfolioCharacteristics: {
        "Reference Index": "Reference Index**",
      },
      maturity: {
        "Reference Index": "Reference Index**",
      },
      Maturity: {
        "Reference Index": "Reference Index**",
      },
      creditQuality: {
        "Reference Index": "Reference Index**",
      },
      "Credit Quality": {
        "Reference Index": "Reference Index**",
      },
      "Key Rate": {
        "Reference Index": "Reference Index**",
      },
      keyRateDurations: {
        "Reference Index": "Reference Index**",
      },
    },
  },
  tbill: {
    cash: {
      "portfolioCharacteristics-os": {
        "Reference Index": "Reference Index",
        "Target Portfolio1": "Target Portfolio",
        Model2: "Sample Representative Portfolio",
      },
      portfolioCharacteristics: {
        "Reference Index": "Reference Index**",
        "Target Portfolio1": "Target Portfolio*",
        Model2: "Sample Representative Portfolio",
      },
      maturity: {
        "Target Portfolio1": "Target Portfolio[1]",
      },
      Maturity: {
        "Target Portfolio1": "Target Portfolio*",
      },
      creditQuality: {
        "Target Portfolio1": "Target Portfolio[1]",
      },
      "Credit Quality": {
        "Target Portfolio1": "Target Portfolio[1]",
      },
    },
  },
};

export const REPORT_SUBHEADING_OVERRIDES = {
  muni: {
    transition: {
      "portfolioCharacteristics-os": {
        "Maintain and Reinvest Proceeds": "Option 0",
      },
      portfolioCharacteristics: {
        "Maintain and Reinvest Proceeds": "Option 0",
      },
    },
  },
  corp: {
    transition: {
      "portfolioCharacteristics-os": {
        "Maintain and Reinvest Proceeds": "Option 0",
      },
      portfolioCharacteristics: {
        "Maintain and Reinvest Proceeds": "Option 0",
      },
      esgRating: {
        "Maintain and Reinvest Proceeds": "Option 0",
      },
    },
  },
};

export const REPORT_ROW_OVERRIDES = {
  equity: {
    cash: {
      "Attribution of Tracking Error": {
        "Style Risk": "Style Risk**",
        "Sector Risk": "Sector Risk**",
      },
      portfolioCharacteristics: {
        "Number of Securities": "Number of Securities***",
      },
      "portfolioCharacteristics-ose": {
        "Number of Securities": "Number of Securities",
      },
    },
    transition: {
      portfolioCharacteristics: {
        "Average Weighted Market Cap (Billions $)":
          "Average Weighted Market Cap (Billions $)*****",
        "Large Cap (%)": "Large Cap (%)*****",
        "Mid Cap (%)": "Mid Cap (%)*****",
        "Small Cap (%)": "Small Cap (%)*****",
        "Defensive Sectors (%)": "Defensive Sectors (%)*****",
        "Highly Cyclical Sectors (%)": "Highly Cyclical Sectors (%)*****",
        "Moderately Cyclical Sectors (%)":
          "Moderately Cyclical Sectors (%)*****",
      },
    },
  },
  treasury: {
    cash: {
      portfolioCharacteristics: {
        "Beta to Index": "Beta to Index",
      },
    },
  },
  corp: {
    cash: {
      portfolioCharacteristics: {
        "Beta to Index": "Beta to Index",
      },
    },
  },
  tbill: {
    cash: {
      portfolioCharacteristics: {
        "Beta to Index": "Beta to Index",
      },
    },
  },
  muni: {
    cash: {
      portfolioCharacteristics: {
        "Beta to Index": "Beta to Index",
      },
    },
  },
};

export const TRANSITION_SCENARIOS_OVERRIDES = {
  transition: {
    equity: {
      "Style (Risk Exposure)": "Transition Scenarios***",
      "Sectors (Risk Exposure)": "Transition Scenarios***",
      "Sectors (GICS)": "Transition Scenarios***",
      proposedSells: "Transition Scenarios** (% SOLD)",
    },
    corp: {
      proposedSells: "Transition Scenarios** (% SOLD)",
    },
  },
};

export const REMI_TEAM_SPONSOR = "INT";
